import { beirutDayStartUtcMs } from '../domain/eventTime';
import { CATALOG_CAZA_BSHARRI, CATALOG_CAZA_ZGHARTA, normalizeCatalogCaza } from '../domain/tourismRules';

export function getCatalogItems({ places = [], businesses = [] } = {}) {
  return [
    ...places.map(place => ({ ...place, type: 'place' })),
    ...businesses.map(business => ({ ...business, type: 'business' })),
  ];
}

const APPROVED_COPY_LABELS = new Set([
  'approved first-party copy',
  'first-party approved',
]);

function hasApprovedCopySignal(item) {
  return (item?.editorialLabels || []).some(label => APPROVED_COPY_LABELS.has(String(label).trim().toLowerCase()));
}

export function catalogDisplayTrustRank(item) {
  if (item?.catalogLocalityNeedsReview || item?.catalogLocalityFilterable === false) return -1;
  if (hasApprovedCopySignal(item)) return 2;
  return 0;
}

function spreadRankedItems(items) {
  const groups = new Map();
  const groupOrder = [];
  items.forEach(item => {
    const key = item.category || item.type || 'other';
    if (!groups.has(key)) {
      groups.set(key, []);
      groupOrder.push(key);
    }
    groups.get(key).push(item);
  });

  const spread = [];
  while (spread.length < items.length) {
    groupOrder.forEach(key => {
      const group = groups.get(key);
      if (group?.length) spread.push(group.shift());
    });
  }
  return spread;
}

export function sortCatalogItemsForDisplay(items) {
  const ranked = [...items].sort((a, b) => {
    const trustDiff = catalogDisplayTrustRank(b) - catalogDisplayTrustRank(a);
    if (trustDiff !== 0) return trustDiff;
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return (b.rating || 0) - (a.rating || 0);
  });

  return [2, 0, -1].flatMap(rank => (
    spreadRankedItems(ranked.filter(item => catalogDisplayTrustRank(item) === rank))
  ));
}

export function getExploreCatalogItems(catalog) {
  return sortCatalogItemsForDisplay(getCatalogItems(catalog));
}

function hasFilterValues(values) {
  return Array.isArray(values) && values.length > 0;
}

const CATEGORY_SEARCH_TERMS = {
  cafe: ['cafe', 'coffee', 'espresso', 'dessert', 'break', 'مقهى', 'قهوة', 'حلويات', 'استراحة'],
  heritage: ['heritage', 'history', 'historic', 'old town', 'souk', 'museum', 'تراث', 'تاريخ', 'سوق', 'متحف'],
  hotel: ['hotel', 'stay', 'lodging', 'guesthouse', 'chalet', 'sleep', 'إقامة', 'فندق', 'مبيت', 'غست هاوس', 'شاليه'],
  nature: ['nature', 'mountain', 'hiking', 'trail', 'forest', 'view', 'viewpoint', 'walk', 'طبيعة', 'جبل', 'مشي', 'مسار', 'غابة', 'إطلالة'],
  religious: ['religious', 'church', 'monastery', 'chapel', 'faith', 'pilgrimage', 'دير', 'كنيسة', 'مزار', 'ديني'],
  restaurant: ['restaurant', 'food', 'dining', 'lunch', 'dinner', 'kibbeh', 'مطعم', 'طعام', 'غداء', 'عشاء', 'كبة'],
  shop: ['shop', 'shopping', 'gift', 'bakery', 'patisserie', 'boutique', 'craft', 'محل', 'تسوق', 'هدايا', 'فرن', 'باتيسري'],
  health: ['health', 'hospital', 'pharmacy', 'clinic', 'doctor', 'dentist', 'medical', 'صحة', 'مستشفى', 'صيدلية', 'عيادة', 'طبيب'],
};

function compactSearchValues(values) {
  return values
    .flatMap(value => Array.isArray(value) ? value : [value])
    .filter(value => value != null && String(value).trim())
    .map(value => String(value).trim());
}

function normalizeSearchText(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();
}

function itemSearchText(item) {
  return normalizeSearchText(compactSearchValues([
    item.name,
    item.nameAr,
    item.village,
    item.villageAr,
    item.category,
    CATEGORY_SEARCH_TERMS[item.category],
    item.description,
    item.descriptionAr,
    item.editorNote,
    item.editorNoteAr,
    item.editorialLabels,
    item.specialties,
    item.priceRange,
    item.phone,
    item.website,
    item.type,
  ]).join(' '));
}

function matchesCatalogSearch(item, searchQuery) {
  const query = normalizeSearchText(searchQuery);
  if (!query) return true;

  const haystack = itemSearchText(item);
  const terms = query.split(/\s+/).filter(Boolean);
  return terms.every(term => haystack.includes(term));
}

function matchesCategoryFilter(item, categoryIds = []) {
  return !hasFilterValues(categoryIds) || categoryIds.includes(item.category);
}

// A row whose caza is missing or unrecognised gets its OWN namespace, never Zgharta's. The repo
// rule of record is that unknown ownership must refuse rather than default (#379): filing such a
// village under the Zgharta header would be a cross-attribution claim made by the filter UI. The
// group renders with the neutral "North Lebanon" label and a neutral tint. Production carries no
// such row today (places 179+112, businesses 392+89, events 17 — all caza-tagged), so this group
// is normally absent; it exists so a future untagged row degrades honestly instead of silently.
export const CATALOG_CAZA_UNKNOWN_GROUP = 'unknown';
const CAZA_VILLAGE_GROUP_ORDER = [CATALOG_CAZA_ZGHARTA, CATALOG_CAZA_BSHARRI, CATALOG_CAZA_UNKNOWN_GROUP];

export function catalogVillageFilterId(item) {
  const village = item?.village;
  if (!village) return null;
  const caza = normalizeCatalogCaza(item?.caza) || CATALOG_CAZA_UNKNOWN_GROUP;
  return `${caza}:${village}`;
}

export function parseCatalogVillageFilterId(value) {
  const raw = String(value || '');
  const sep = raw.indexOf(':');
  if (sep <= 0) return { id: raw, caza: null, village: raw };
  const prefix = raw.slice(0, sep);
  const caza = normalizeCatalogCaza(prefix);
  const village = raw.slice(sep + 1);
  if (!village) return { id: raw, caza: null, village: raw };
  // The unknown-ownership namespace is a real prefix, not a malformed id: strip it so the village
  // still matches, but report caza `null` so matching never asserts an owning caza for the row.
  if (!caza) {
    if (prefix === CATALOG_CAZA_UNKNOWN_GROUP) {
      return { id: `${CATALOG_CAZA_UNKNOWN_GROUP}:${village}`, caza: null, village, unknownCaza: true };
    }
    return { id: raw, caza: null, village: raw };
  }
  return { id: `${caza}:${village}`, caza, village };
}

function itemMatchesVillageFilterId(item, filterId) {
  const parsed = parseCatalogVillageFilterId(filterId);
  if (!parsed?.village || item?.village !== parsed.village) return false;
  const itemCaza = normalizeCatalogCaza(item?.caza);
  // An unknown-ownership filter selects exactly the rows that are themselves unowned.
  if (parsed.unknownCaza) return !itemCaza;
  if (!parsed.caza) return true;
  return itemCaza === parsed.caza;
}

function matchesVillageFilter(item, villageIds = []) {
  if (!hasFilterValues(villageIds)) return true;
  return item.catalogLocalityFilterable !== false
    && villageIds.some(id => itemMatchesVillageFilterId(item, id));
}

function matchesMinimumRating(item, minRating = 0) {
  return !minRating || (item.rating && item.rating >= minRating);
}

function matchesAreaFilter(item, zoneId) {
  if (!zoneId) return true;
  if (zoneId === '__review__') return Boolean(item.geozone && item.geozone.status !== 'inside');
  return item.geozone?.zoneIds?.includes(zoneId) || false;
}

export function filterExploreCatalogItems(
  items,
  { searchQuery = '', categoryIds = [], villageIds = [], minRating = 0, zoneId = '' } = {}
) {
  return items
    .filter(item => matchesCategoryFilter(item, categoryIds))
    .filter(item => matchesMinimumRating(item, minRating))
    .filter(item => matchesVillageFilter(item, villageIds))
    .filter(item => matchesAreaFilter(item, zoneId))
    .filter(item => matchesCatalogSearch(item, searchQuery));
}

export function queryExploreCatalogItems(catalog, filters = {}) {
  return filterExploreCatalogItems(getExploreCatalogItems(catalog), filters);
}

export function getCatalogVillages(items) {
  return [...new Set(
    items
      .filter(item => item.catalogLocalityFilterable !== false)
      .map(item => item.village)
      .filter(Boolean)
  )].sort();
}

export function countCatalogItemsByVillage(items, village, { categoryIds = [] } = {}) {
  return items
    .filter(item => item.catalogLocalityFilterable !== false)
    .filter(item => itemMatchesVillageFilterId(item, village))
    .filter(item => matchesCategoryFilter(item, categoryIds))
    .length;
}

export function getCatalogVillageGroups(items, { categoryIds = [] } = {}) {
  const groups = Object.fromEntries(CAZA_VILLAGE_GROUP_ORDER.map(caza => [caza, {
    caza,
    villages: [],
  }]));
  const seen = Object.fromEntries(CAZA_VILLAGE_GROUP_ORDER.map(caza => [caza, new Set()]));

  (items || [])
    .filter(item => item?.catalogLocalityFilterable !== false && item?.village)
    .forEach(item => {
      const caza = normalizeCatalogCaza(item.caza) || CATALOG_CAZA_UNKNOWN_GROUP;
      const group = groups[caza];
      if (!group) return;
      const id = catalogVillageFilterId(item);
      if (!id || seen[caza].has(id)) return;
      seen[caza].add(id);
      group.villages.push({
        id,
        village: item.village,
        villageAr: item.villageAr || item.village,
        count: countCatalogItemsByVillage(items, id, { categoryIds }),
      });
    });

  return CAZA_VILLAGE_GROUP_ORDER
    .map(caza => {
      const group = groups[caza];
      group.villages.sort((a, b) => a.village.localeCompare(b.village));
      return group;
    })
    .filter(group => group.villages.length > 0);
}

export function hasUsableCoordinates(item) {
  const coords = item?.coordinates || item;
  return (
    typeof coords?.lat === 'number' &&
    typeof coords?.lng === 'number' &&
    Number.isFinite(coords.lat) &&
    Number.isFinite(coords.lng)
  );
}

export function getMapCatalogLocations(catalog) {
  return getCatalogItems(catalog).filter(hasUsableCoordinates);
}

export function filterMapCatalogLocations(locations, { categoryIds = [], villageIds = [], zoneId = '' } = {}) {
  return locations
    .filter(item => matchesCategoryFilter(item, categoryIds))
    .filter(item => matchesVillageFilter(item, villageIds))
    .filter(item => matchesAreaFilter(item, zoneId));
}

export function queryMapCatalogLocations(catalog, filters = {}) {
  return filterMapCatalogLocations(getMapCatalogLocations(catalog), filters);
}

export function findCatalogLocationBySearch(locations, searchQuery) {
  // TRIMMED before the length gate. Untrimmed, three spaces passed a gate meant to hold back
  // one- and two-character queries, and `matchesCatalogSearch` then trims the same string to
  // empty — which matches EVERYTHING, so the first row in the catalog was returned as a "match".
  // Harmless-looking before #442 (it panned the map for no reason); once a match can clear the
  // visitor's filters, it had to go (cold review, #442).
  const query = normalizeSearchText(searchQuery);
  if (query.length <= 2) return null;

  return locations.find(location => (
    matchesCatalogSearch(location, searchQuery)
  )) || null;
}

// The map search box searches the WHOLE catalog while the carousel's pool is the FILTERED set,
// so a match the active filters exclude used to be panned to and then dropped by the next map
// idle (#435, residue decided in #442). Owner decision 2026-08-27: clear the filters — but only
// when there really is a match AND that match is really excluded, never on empty or no-match
// input, because this runs on every keystroke.
//
// Pure on purpose: the handler in `src/App.js` is an `onChange` one-liner, and this is the part
// worth pinning in the unit suite.
export function planMapSearchSelection(allLocations, filteredLocations, searchQuery) {
  const match = findCatalogLocationBySearch(allLocations, searchQuery);
  if (!match) return { match: null, clearsFilters: false };

  const withinFilters = (filteredLocations || []).some(location => (
    location.type === match.type && String(location.id) === String(match.id)
  ));

  return { match, clearsFilters: !withinFilters };
}

export function getDistanceKm(a, b) {
  if (a?.lat == null || a?.lng == null || b?.lat == null || b?.lng == null) return Infinity;

  const R = 6371;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const x = Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * Math.PI / 180) *
    Math.cos(b.lat * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;

  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export function getNearbyCatalogItems(catalog, coords, excludeId, limit = 4) {
  return getCatalogItems(catalog)
    .filter(item => (
      (item.id !== excludeId || item.coordinates?.lat !== coords?.lat || item.coordinates?.lng !== coords?.lng)
      && item.coordinates?.lat != null
    ))
    .map(item => ({ ...item, dist: getDistanceKm(coords, item.coordinates) }))
    .sort((a, b) => a.dist - b.dist)
    .slice(0, limit);
}

export function getTopVerifiedBusinesses(businesses = [], { limit = 6 } = {}) {
  return businesses
    .filter(business => business.verified)
    .sort((a, b) => (b.rating || 0) - (a.rating || 0))
    .slice(0, limit);
}

export function countCatalogItemsInVillage(catalog, village) {
  return getCatalogItems(catalog).filter(item => item.village === village).length;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function resolveEventDateRange({ dateFilter = 'all', dateRange, now = new Date() } = {}) {
  if (dateRange) {
    const start = beirutDayStartUtcMs(dateRange.start);
    const end = beirutDayStartUtcMs(dateRange.end);
    return {
      start: start ?? -Infinity,
      end: end == null ? Infinity : end + DAY_MS,
    };
  }

  const nowDay = beirutDayStartUtcMs(now) ?? beirutDayStartUtcMs(new Date());
  if (dateFilter === 'today') {
    return { start: nowDay, end: nowDay + DAY_MS };
  }
  if (dateFilter === 'weekend') {
    const nowDate = new Date(nowDay);
    const weekday = nowDate.getUTCDay();
    const daysFromFriday = weekday === 0 ? -2 : (weekday <= 5 ? 5 - weekday : -1);
    const start = nowDay + (daysFromFriday * DAY_MS);
    return { start, end: start + (3 * DAY_MS) };
  }
  if (dateFilter === 'month') {
    const nowDate = new Date(nowDay);
    const start = Date.UTC(nowDate.getUTCFullYear(), nowDate.getUTCMonth(), 1);
    const end = Date.UTC(nowDate.getUTCFullYear(), nowDate.getUTCMonth() + 1, 1);
    return { start, end };
  }
  return null;
}

// The LAST day an event runs. A multi-day festival is over only after its end date, which is
// the same rule `isPastEvent` applies in the dashboard (src/admin/adminRows.js) — the two
// surfaces must not disagree about whether a row has already happened. `endDate` is optional,
// so a one-day event falls back to its own date, unchanged.
function eventLastDayMs(event) {
  return beirutDayStartUtcMs(event?.endDate || event?.date);
}

export function filterEvents(events = [], { categoryId = 'all', timeFilter = 'all', dateFilter = 'all', dateRange, now = new Date() } = {}) {
  const nowDay = beirutDayStartUtcMs(now) ?? beirutDayStartUtcMs(new Date());
  const resolvedDateRange = resolveEventDateRange({ dateFilter, dateRange, now });
  return events
    .filter(event => categoryId === 'all' || event.category === categoryId)
    .filter(event => {
      if (!resolvedDateRange) return true;
      // The today/weekend/month windows still key off the day the event STARTS — they answer
      // "what begins in this window", which is a different question from "is it over".
      const eventDay = beirutDayStartUtcMs(event.date);
      return eventDay != null && eventDay >= resolvedDateRange.start && eventDay < resolvedDateRange.end;
    })
    .filter(event => {
      if (timeFilter === 'all') return true;
      const lastDay = eventLastDayMs(event);
      if (lastDay == null) return false;
      if (timeFilter === 'upcoming') return lastDay >= nowDay;
      if (timeFilter === 'past') return lastDay < nowDay;
      return true;
    });
}

export function filterLocationsByViewport(locations = [], contains) {
  if (typeof contains !== 'function') return [];
  return locations.filter(location => contains({
    lat: location.coordinates.lat,
    lng: location.coordinates.lng,
  }));
}

export function getFavoritedCatalogItems(catalog, favs = {}) {
  const placeIds = new Set(favs.places || []);
  const businessIds = new Set(favs.businesses || []);
  const places = (catalog?.places || []).filter(place => placeIds.has(place.id));
  const businesses = (catalog?.businesses || []).filter(business => businessIds.has(business.id));
  return getCatalogItems({ places, businesses });
}
